import React from "react";

/**
 * Tiny, dependency-free Markdown renderer for AI chat replies.
 *
 * Supports the subset the Jash agent actually returns: headings, bold,
 * inline code, bullet/numbered lists, GitHub-style tables, and paragraphs.
 * It builds React elements (never dangerouslySetInnerHTML), so model output
 * cannot inject HTML/scripts — it is XSS-safe by construction.
 */

// Inline formatting: **bold**, *italic*, `code`. Returns an array of React nodes.
function renderInline(text, keyPrefix = "i") {
  const nodes = [];
  // Split on the three inline tokens while keeping the delimiters.
  const regex = /(\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`)/g;
  let last = 0;
  let m;
  let idx = 0;
  while ((m = regex.exec(text)) !== null) {
    if (m.index > last) nodes.push(text.slice(last, m.index));
    const tok = m[0];
    const key = `${keyPrefix}-${idx++}`;
    if (tok.startsWith("**")) {
      nodes.push(<strong key={key}>{tok.slice(2, -2)}</strong>);
    } else if (tok.startsWith("`")) {
      nodes.push(
        <code key={key} className="rounded bg-gray-100 px-1 py-0.5 text-[12px] text-indigo-700">
          {tok.slice(1, -1)}
        </code>,
      );
    } else {
      nodes.push(<em key={key}>{tok.slice(1, -1)}</em>);
    }
    last = m.index + tok.length;
  }
  if (last < text.length) nodes.push(text.slice(last));
  return nodes;
}

function isTableSeparator(line) {
  // | --- | :---: | ---: |
  return /^\s*\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)+\|?\s*$/.test(line);
}

function splitRow(line) {
  return line
    .trim()
    .replace(/^\|/, "")
    .replace(/\|$/, "")
    .split("|")
    .map((c) => c.trim());
}

const ChatMarkdown = ({ text }) => {
  const lines = (text || "").split("\n");
  const blocks = [];
  let i = 0;
  let key = 0;

  while (i < lines.length) {
    const line = lines[i];

    // Table: a header row followed by a separator row.
    if (
      line.includes("|") &&
      i + 1 < lines.length &&
      isTableSeparator(lines[i + 1])
    ) {
      const header = splitRow(line);
      const rows = [];
      i += 2;
      while (i < lines.length && lines[i].includes("|") && lines[i].trim() !== "") {
        rows.push(splitRow(lines[i]));
        i += 1;
      }
      blocks.push(
        <div key={key++} className="my-2 overflow-x-auto">
          <table className="min-w-full border border-gray-200 text-xs">
            <thead className="bg-indigo-50 text-indigo-800">
              <tr>
                {header.map((h, hi) => (
                  <th key={hi} className="border border-gray-200 px-2 py-1 text-left font-semibold">
                    {renderInline(h, `th${key}-${hi}`)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, ri) => (
                <tr key={ri} className={ri % 2 ? "bg-gray-50" : "bg-white"}>
                  {header.map((_, ci) => (
                    <td key={ci} className="border border-gray-200 px-2 py-1 align-top">
                      {renderInline(r[ci] || "", `td${key}-${ri}-${ci}`)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>,
      );
      continue;
    }

    // Heading: #, ##, ###
    const h = line.match(/^(#{1,3})\s+(.*)$/);
    if (h) {
      const level = h[1].length;
      const cls =
        level === 1 ? "text-sm font-bold" : level === 2 ? "text-sm font-semibold" : "text-xs font-semibold";
      blocks.push(
        <p key={key++} className={`${cls} mt-2 text-gray-900`}>
          {renderInline(h[2], `h${key}`)}
        </p>,
      );
      i += 1;
      continue;
    }

    // Unordered list block
    if (/^\s*[-*]\s+/.test(line)) {
      const items = [];
      while (i < lines.length && /^\s*[-*]\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*[-*]\s+/, ""));
        i += 1;
      }
      blocks.push(
        <ul key={key++} className="my-1 ml-4 list-disc space-y-0.5">
          {items.map((it, ii) => (
            <li key={ii}>{renderInline(it, `li${key}-${ii}`)}</li>
          ))}
        </ul>,
      );
      continue;
    }

    // Ordered list block
    if (/^\s*\d+\.\s+/.test(line)) {
      const items = [];
      while (i < lines.length && /^\s*\d+\.\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*\d+\.\s+/, ""));
        i += 1;
      }
      blocks.push(
        <ol key={key++} className="my-1 ml-4 list-decimal space-y-0.5">
          {items.map((it, ii) => (
            <li key={ii}>{renderInline(it, `ol${key}-${ii}`)}</li>
          ))}
        </ol>,
      );
      continue;
    }

    // Blank line -> spacing
    if (line.trim() === "") {
      i += 1;
      continue;
    }

    // Paragraph (join consecutive plain lines)
    const buf = [line];
    i += 1;
    while (
      i < lines.length &&
      lines[i].trim() !== "" &&
      !/^\s*[-*]\s+/.test(lines[i]) &&
      !/^\s*\d+\.\s+/.test(lines[i]) &&
      !/^(#{1,3})\s+/.test(lines[i]) &&
      !(lines[i].includes("|") && i + 1 < lines.length && isTableSeparator(lines[i + 1]))
    ) {
      buf.push(lines[i]);
      i += 1;
    }
    blocks.push(
      <p key={key++} className="whitespace-pre-wrap">
        {renderInline(buf.join("\n"), `p${key}`)}
      </p>,
    );
  }

  return <div className="space-y-1">{blocks}</div>;
};

export default ChatMarkdown;
